import { useContext } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { AdminEmailPrefKey } from "@eip/shared/types";
import Toggle from "../../Components/Input/Toggle";
import { LangContext, LangContextType } from "../../utilities/customHooks";
import { queryKeys } from "../../utilities/queryKeys";
import { getAdminEmailPreferences } from "../../services/admin/emailPreferencesService";
import { useUpdateAdminEmailPreferences } from "../../services/admin/profileMutations";
import textData from "../../textData.json";

type PrefGroup = {
  headingKey: "RegionBroadcastsGroup" | "AssignedToMeGroup";
  prefs: { key: AdminEmailPrefKey; labelKey: PrefTextKey; descKey: PrefTextKey }[];
};

type PrefTextKey = keyof typeof textData.Pages.Settings.EmailPreferences;

const PREF_GROUPS: PrefGroup[] = [
  {
    headingKey: "RegionBroadcastsGroup",
    prefs: [
      { key: "newApplication", labelKey: "PrefNewApplication", descKey: "PrefNewApplicationDesc" },
      { key: "revisedApplication", labelKey: "PrefRevisedApplication", descKey: "PrefRevisedApplicationDesc" },
    ],
  },
  {
    headingKey: "AssignedToMeGroup",
    prefs: [
      { key: "sowSubmitted", labelKey: "PrefSowSubmitted", descKey: "PrefSowSubmittedDesc" },
      { key: "sowFullySigned", labelKey: "PrefSowFullySigned", descKey: "PrefSowFullySignedDesc" },
      { key: "retainerInvoiceSubmitted", labelKey: "PrefRetainerInvoice", descKey: "PrefRetainerInvoiceDesc" },
      { key: "finalInvoiceSubmitted", labelKey: "PrefFinalInvoice", descKey: "PrefFinalInvoiceDesc" },
      { key: "programApplicationReceived", labelKey: "PrefProgramApplication", descKey: "PrefProgramApplicationDesc" },
    ],
  },
];

/**
 * Personal-settings email notification toggles. State renders straight from
 * the suspense query (server returns the fully-resolved 7-key map), and each
 * flip PUTs a single-key partial so sibling preferences are never clobbered.
 */
export default function AdminEmailPreferences() {
  const { langState } = useContext(LangContext) as LangContextType;
  const t = textData.Pages.Settings.EmailPreferences;

  const { data } = useSuspenseQuery({
    queryKey: queryKeys.adminProfiles.emailPreferences(),
    queryFn: getAdminEmailPreferences,
  });

  const updatePrefs = useUpdateAdminEmailPreferences(
    t.PrefsSaveSuccess[langState],
    t.PrefsSaveFail[langState]
  );

  return (
    // pb (not mb): bottom margin on the last child is excluded from the scroll
    // container's scrollable extent, so the page ended flush at the last toggle
    <div className="self-baseline w-full flex flex-col gap-8 pb-16">
      {/* "Email Notifications" itself renders as the Personal subtab in
          Settings.tsx — only the helper line lives here */}
      <p className="text-sm text-text/50 -mb-2">{t.EmailNotificationsDescription[langState]}</p>

      {PREF_GROUPS.map((group, groupIndex) => (
        <div
          key={group.headingKey}
          className={`flex flex-col gap-5 ${groupIndex > 0 ? "border-t border-text/10 pt-6" : ""}`}
        >
          <p className="text-base font-bold uppercase tracking-wider text-text/70">{t[group.headingKey][langState]}</p>
          {group.prefs.map(({ key, labelKey, descKey }) => (
            <div key={key}>
              <Toggle
                key={`${key}-${data.preferences[key]}`}
                label={t[labelKey][langState]}
                isDefaultChecked={data.preferences[key]}
                toggleActionCallback={(isChecked) => updatePrefs.mutate({ [key]: isChecked })}
                checkedBg="bg-cSuccess"
                uncheckedBg="bg-cMediumGrey"
              />
              <p className="text-sm text-text/50 mt-1 ml-1">{t[descKey][langState]}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
